import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import { Skeleton } from "primereact/skeleton";
import { Button } from "primereact/button";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import { AxiosError } from "axios";
import { errorHandler } from "../../utils/HelperFunctions";
import AdvertisementCard from "../../components/common/AdvertisementCard";
import { Dropdown } from "primereact/dropdown";
import { Tooltip } from "primereact/tooltip";
import { Tag } from "primereact/tag";
import { Dialog } from "primereact/dialog";
import { Calendar } from "primereact/calendar";

const AdvertisementView: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const activeOptions = [
        { label: "Active", value: true },
        { label: "Inactive", value: false },
    ];

    const [record, setRecord] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);

    const pageNameLabels: Record<string, string> = {
        all: "All",
        homepost: "Home Post",
        homestory: "Home Story",
        maitrimonial: "MaitriMonial",
        users: "Directory",
        business: "Marketplace",
        bazarbhav: "Bazar Bhav",
        job: "Jobs",
        bloodgroup: "Blood Group",
        notification: "Notification",
    };

    const [rerunDialog, setRerunDialog] = useState(false);
    const [rerunStartDate, setRerunStartDate] = useState<Date | null>(null);
    const [rerunEndDate, setRerunEndDate] = useState<Date | null>(null);
    const [rerunLoading, setRerunLoading] = useState(false);

    const [userAds, setUserAds] = useState<any[]>([]);
    const [userAdsLoading, setUserAdsLoading] = useState(false);

    const handleRerun = async () => {
        if (!rerunStartDate || !rerunEndDate) {
            showToast({ severity: "warn", summary: "Warning", detail: "Please select start and end date", life: 3000 });
            return;
        }

        try {
            setRerunLoading(true);

            const formatDate = (d: Date) => {
                const day = String(d.getDate()).padStart(2, "0");
                const month = String(d.getMonth() + 1).padStart(2, "0");
                return `${d.getFullYear()}-${month}-${day}`;
            };

            const response = await apiFunctions.put(`/advertisement/rerun/${record._id}`, {
                start_date: formatDate(rerunStartDate),
                end_date: formatDate(rerunEndDate),
            });

            if (response?.data?.success) {
                setRecord((prev: any) => ({
                    ...prev,
                    start_date: rerunStartDate,
                    end_date: rerunEndDate,
                    is_active: true,
                }));
                setRerunDialog(false);
                setRerunStartDate(null);
                setRerunEndDate(null);
                showToast({ severity: "success", summary: "Success", detail: "Advertisement rerun successfully", life: 3000 });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setRerunLoading(false);
        }
    };

    const fetchUserAds = async (userId: string) => {
        try {
            setUserAdsLoading(true);
            const response = await apiFunctions.get(`/advertisement/user/${userId}?limit=5`);
            if (response?.data?.success) {
                setUserAds(response.data.data.filter((ad: any) => ad._id !== id));
            }
        } catch (err) {
            console.error(err);
        } finally {
            setUserAdsLoading(false);
        }
    };

    const getAdvertisementData = async (id: string) => {
        try {
            setLoading(true);

            const response = await apiFunctions.get(`/advertisement/${id}`);

            if (response?.data?.success) {
                setRecord(response.data.data);   // ✅ FIXED
            } else {
                showToast({
                    severity: "error",
                    summary: "Error",
                    detail: response.data.message,
                    life: 3000,
                });
            }

        } catch (err) {
            errorHandler(err as AxiosError);
        } finally {
            setLoading(false);
        }
    };

    const handleStatusChange = async (value: boolean) => {
        try {
            const response = await apiFunctions.put(`/advertisement/status/${record._id}`, {
                is_active: value,
            });

            if (response?.data?.success) {
                setRecord((prev: any) => ({
                    ...prev,
                    is_active: value,
                }));

                showToast({
                    severity: "success",
                    summary: "Updated",
                    detail: "Advertisement status updated successfully",
                    life: 3000,
                });
            }
        } catch (err) {
            errorHandler(err as AxiosError);
        }
    };

    useEffect(() => {
        if (id) {
            getAdvertisementData(id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (record?.user_id) {
            fetchUserAds(record.user_id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record?.user_id]);

    return (
        <div className="dashboard-margin">
            <div className="mb-4 flex items-center justify-between">
                <h2>View Advertisement</h2>
                {record && !record.is_active && record.end_date && new Date(record.end_date) < new Date() && (
                    <Button
                        label="Rerun Advertisement"
                        icon="pi pi-refresh"
                        severity="info"
                        onClick={() => setRerunDialog(true)}
                    />
                )}
            </div>

            {/* Bazar Bhav Info Card */}
            <Card className="mb-6">
                {loading ? (
                    <div className="space-y-2">
                        <Skeleton width="50%" height="2rem" />
                        <Skeleton width="70%" height="1.5rem" />
                        <Skeleton width="30%" height="1.5rem" />
                    </div>
                ) : (
                    <div className="grid grid-cols-12 gap-4">

                        <div className="lg:col-span-6 col-span-12"><strong>Company Name:</strong> {record?.company_name}</div>
                        <div className="lg:col-span-6 col-span-12">
                            <strong>Page Name:</strong>

                            <div className="flex flex-wrap gap-2 mt-2">
                                {Array.isArray(record?.page_name) && record.page_name.length > 0 ? (
                                    record.page_name.map((page: string, index: number) => (
                                        <Tag
                                            key={index}
                                            value={pageNameLabels[page] || page}
                                            severity="info"
                                        />
                                    ))
                                ) : (
                                    <span>-</span>
                                )}
                            </div>
                        </div>
                        <div className="lg:col-span-6 col-span-12"><strong>Ad Type:</strong> {record?.ad_type === "image" ? "Banner" : "Video"}</div>
                        <div className="lg:col-span-6 col-span-12 flex flex-col lg:flex-row lg:items-center gap-2">
                            <strong>Payment Status:</strong>
                            <div className="flex item-center gap-2">
                                <Tag
                                    value={record?.payment_status}
                                    severity={
                                        record?.payment_status === "success"
                                            ? "success"
                                            : record?.payment_status === "failed"
                                                ? "danger"
                                                : "warning"
                                    }
                                />

                                <InformationCircleIcon
                                    className="w-4 h-4 text-purple-400 cursor-pointer payment-tooltip"
                                />

                                <Tooltip
                                    target=".payment-tooltip"
                                    content="Shows transaction result from payment gateway."
                                    position="top"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12"><strong>Mobile:</strong> {record?.mobile_number}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>WhatsApp:</strong> {record?.whatsapp_number}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Email:</strong> {record?.email || "-"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Target City:</strong> {record?.target_city || "-"}</div>

                        <div className="lg:col-span-6 col-span-12 flex items-center gap-2">
                            <strong>Start Date:</strong>
                            <div className="flex item-center gap-2">

                                {record?.start_date
                                    ? new Date(record.start_date).toLocaleDateString("en-GB")
                                    : "-"}

                                <InformationCircleIcon
                                    className="w-4 h-4 text-blue-400 cursor-pointer start-tooltip"
                                />

                                <Tooltip
                                    target=".start-tooltip"
                                    content="Advertisement becomes visible from this date."
                                    position="top"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12 flex items-center gap-2">
                            <strong>End Date:</strong>
                            <div className="flex item-center gap-2">

                                {record?.end_date
                                    ? new Date(record.end_date).toLocaleDateString("en-GB")
                                    : "-"}

                                <InformationCircleIcon
                                    className="w-4 h-4 text-blue-400 cursor-pointer end-tooltip"
                                />

                                <Tooltip
                                    target=".end-tooltip"
                                    content="Advertisement automatically expires after this date."
                                    position="top"
                                />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12"><strong>Active:</strong> {record?.is_active ? "Yes" : "No"}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Views:</strong> {record?.views}</div>

                        <div className="lg:col-span-6 col-span-12"><strong>WhatsApp Clicks:</strong> {record?.whatsapp_clicks}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>Call Clicks:</strong> {record?.call_clicks}</div>

                        <div className="lg:col-span-6 col-span-12"><strong>User Name:</strong> {record?.full_name}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>User Mobile:</strong> {record?.mobile_no}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>City:</strong> {record?.city}</div>
                        <div className="lg:col-span-6 col-span-12"><strong>User Type:</strong> {record?.user_type}</div>

                        <div className="lg:col-span-6 col-span-12 flex items-center gap-2">
                            <strong>Status:</strong>
                            <div className="flex item-center gap-2">

                                <Tag
                                    value={record?.is_active ? "Active" : "Inactive"}
                                    severity={record?.is_active ? "success" : "danger"}
                                />

                                <InformationCircleIcon
                                    className="w-4 h-4 text-gray-400 cursor-pointer"
                                    data-pr-tooltip="Active ads are visible to users. Inactive ads are hidden."
                                />
                                <Tooltip target=".text-gray-400" position="top" />
                            </div>
                        </div>

                        <div className="lg:col-span-6 col-span-12">
                            <strong>Change Status:</strong>
                            <Dropdown
                                value={record?.is_active}
                                options={activeOptions}
                                onChange={(e) => handleStatusChange(e.value)}
                                className="w-full mt-1"
                            />
                        </div>

                        {record?.media && record.media.length > 0 && (
                            <div className="col-span-12 mt-2">
                                <strong>Advertisement Media:</strong>
                                <div className="flex flex-col items-center mt-4">
                                    <div className="relative w-full max-w-[320px] aspect-[9/16] bg-neutral-900 rounded-[2rem] border-[6px] border-neutral-800 shadow-2xl overflow-hidden ring-4 ring-neutral-200">
                                        <div className="absolute inset-0 overflow-hidden">
                                            <AdvertisementCard
                                                companyName={record.company_name}
                                                media={record.media.map((item: any) => ({ type: item.media_type, url: item.media_url }))}
                                                shortDescription={record.short_description}
                                                whatsappNumber={record.whatsapp_number}
                                                mobileNumber={record.mobile_number}
                                                websiteLink={record.website_link}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                    </div>
                )}
            </Card>
            {/* User's Previous Ads */}
            {record?.user_id && (
                <Card className="mt-4">
                    <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-lg m-0">User's Previous Advertisements</h3>
                    </div>

                    {userAdsLoading ? (
                        <Skeleton width="100%" height="3rem" />
                    ) : userAds.length === 0 ? (
                        <p className="text-gray-500 text-sm">No other advertisements found for this user.</p>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm border-collapse">
                                <thead>
                                    <tr className="bg-gray-100">
                                        <th className="text-left p-2 border-b">Company Name</th>
                                        <th className="text-left p-2 border-b">Ad Type</th>
                                        <th className="text-left p-2 border-b">Status</th>
                                        <th className="text-left p-2 border-b">Start Date</th>
                                        <th className="text-left p-2 border-b">End Date</th>
                                        <th className="text-left p-2 border-b">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {userAds.map((ad: any) => (
                                        <tr key={ad._id} className="hover:bg-gray-50">
                                            <td className="p-2 border-b">{ad.company_name}</td>
                                            <td className="p-2 border-b">{ad.ad_type === "image" ? "Banner" : "Video"}</td>
                                            <td className="p-2 border-b">
                                                <Tag
                                                    value={ad.is_active ? "Active" : "Inactive"}
                                                    severity={ad.is_active ? "success" : "danger"}
                                                />
                                            </td>
                                            <td className="p-2 border-b">
                                                {ad.start_date ? new Date(ad.start_date).toLocaleDateString("en-GB") : "-"}
                                            </td>
                                            <td className="p-2 border-b">
                                                {ad.end_date ? new Date(ad.end_date).toLocaleDateString("en-GB") : "-"}
                                            </td>
                                            <td className="p-2 border-b">
                                                <Button
                                                    label="View"
                                                    link
                                                    size="small"
                                                    style={{ color: "#264164" }}
                                                    onClick={() => navigate(`/admin/advertisement/view/${ad._id}`)}
                                                />
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>
            )}

            {/* Rerun Dialog */}
            <Dialog
                header="Rerun Advertisement"
                visible={rerunDialog}
                style={{ width: "90vw", maxWidth: "420px" }}
                modal
                draggable={false}
                onHide={() => {
                    setRerunDialog(false);
                    setRerunStartDate(null);
                    setRerunEndDate(null);
                }}
            >
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-gray-500 m-0">
                        Advertisement will be reactivated with the new dates.
                    </p>

                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-sm">Start Date</label>
                        <Calendar
                            value={rerunStartDate}
                            onChange={(e) => setRerunStartDate(e.value as Date)}
                            showIcon
                            dateFormat="dd/mm/yy"
                            minDate={new Date()}
                            className="w-full"
                        />
                    </div>

                    <div className="flex flex-col gap-1">
                        <label className="font-semibold text-sm">End Date</label>
                        <Calendar
                            value={rerunEndDate}
                            onChange={(e) => setRerunEndDate(e.value as Date)}
                            showIcon
                            dateFormat="dd/mm/yy"
                            minDate={rerunStartDate || new Date()}
                            className="w-full"
                        />
                    </div>

                    <div className="flex justify-end gap-2 mt-2">
                        <Button
                            label="Cancel"
                            severity="secondary"
                            onClick={() => {
                                setRerunDialog(false);
                                setRerunStartDate(null);
                                setRerunEndDate(null);
                            }}
                            disabled={rerunLoading}
                        />
                        <Button
                            label="Rerun"
                            icon="pi pi-refresh"
                            loading={rerunLoading}
                            onClick={handleRerun}
                        />
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default AdvertisementView;
